import { Session } from '@supabase/supabase-js'
import { SessionContextProvider } from 'app/providers/supabase/SessionContext'
import { createSupabaseBrowserClient } from 'app/utils/supabase/browserClient'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import QonversionService from 'app/utils/qonversion/QonversionService'

import { AuthStateChangeHandler } from './AuthStateChangeHandler'

export type AuthProviderProps = {
  initialSession?: Session | null
  children?: React.ReactNode
}

// Extend the JwtPayload interface to include app_metadata
declare module 'jwt-decode' {
  interface JwtPayload {
    app_metadata?: {
      is_pending_deletion?: boolean;
      has_access?: boolean;
      is_payment_exempt?: boolean;
      [key: string]: any;
    };
  }
}

export const AuthProvider = ({ initialSession, children }: AuthProviderProps) => {
  // Create a new supabase browser client on every first render.
  const [supabaseClient] = useState(() => createSupabaseBrowserClient())
  const router = useRouter()

  // Initialize Qonversion once when the component mounts
  useEffect(() => {
    const qonversionService = QonversionService.getInstance();
    qonversionService.initialize().catch(error => {
      console.error('[AuthProvider] Failed to initialize Qonversion:', error);
    });
  }, [])

  useEffect(() => {
    if (initialSession?.access_token) {
      try {
        // Decode the JWT token
        const decoded = jwtDecode<JwtPayload>(initialSession.access_token)

        // Check if user is pending deletion
        if (decoded?.app_metadata?.is_pending_deletion) {
          router.replace('/pending-delete')
          return
        }

        // Check if user has access
        const hasAccess = decoded?.app_metadata?.has_access === true
        if (!hasAccess) {
          router.replace('/expired')
          return
        }
      } catch (error) {
        console.error('Error decoding JWT token:', error)
      }
    }
  }, [initialSession, router])

  return (
    <SessionContextProvider supabaseClient={supabaseClient} initialSession={initialSession}>
      <AuthStateChangeHandler />
      {children}
    </SessionContextProvider>
  )
}
